/* =============================================================
   NOAH'S — Early Access (Flashy) modal controller
   Vanilla JS, no jQuery. Handles ONLY open/close + accessibility.
   The form itself is submitted/handled by Flashy (class="flashy-form"),
   so we never touch the form's submit event.
   ============================================================= */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-early-access-root]');
    if (!root || root.getAttribute('data-early-access-init') === '1') return;
    root.setAttribute('data-early-access-init', '1');

    var openBtn = root.querySelector('[data-early-access-open]');
    var modal = root.querySelector('[data-early-access-modal]');
    if (!openBtn || !modal) return;

    /* ---- Floating tab visibility ----
       The tab starts hidden (class "is-hidden" in the markup). It only appears
       AFTER the visitor has closed the popup WITHOUT registering — never on the
       first view. That "closed once" state is remembered for the session, so the
       tab also shows straight away on later page views until they register. */
    var tab = root.querySelector('[data-early-access-tab]');
    var dismissBtn = root.querySelector('[data-early-access-dismiss]');

    var CLAIM_KEY = 'early-access-claimed';       // set once the visitor registers
    var CLOSED_KEY = 'early-access-closed-once';   // set once they close without registering

    function isRegistered() {
      try { return localStorage.getItem(CLAIM_KEY) === '1'; } catch (e) { return false; }
    }
    function revealTab() {
      if (tab && !isRegistered()) tab.classList.remove('is-hidden', 'is-dismissed');
    }
    function hideTab() {
      if (tab) tab.classList.add('is-hidden');
    }

    // Always show the floating tab on the side straight away (unless the visitor
    // has already registered, in which case they're done and it stays hidden).
    revealTab();

    if (dismissBtn && tab) {
      dismissBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        e.preventDefault();
        tab.classList.add('is-dismissed');
      });
    }

    var dialog = modal.querySelector('.early-access-modal__dialog');
    var closeEls = modal.querySelectorAll('[data-early-access-close]');
    var lastFocused = null;

    function focusableIn(el) {
      return Array.prototype.slice.call(
        el.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (n) { return n.offsetParent !== null || n === document.activeElement; });
    }

    function open() {
      if (modal.classList.contains('is-open')) return;
      lastFocused = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('early-access-no-scroll');
      // focus the first real field once the open transition starts
      window.setTimeout(function () {
        var first = dialog.querySelector('input:not([type="hidden"])');
        if (first) { try { first.focus(); } catch (e) {} }
      }, 60);
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      if (!modal.classList.contains('is-open')) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('early-access-no-scroll');
      document.removeEventListener('keydown', onKeydown);
      // once the visitor has seen & closed it, never auto-open again this session
      markAutoOpenDone();
      // Closed without registering -> reveal the floating button (and remember it
      // for the session). Registered -> keep it hidden, they're done.
      if (isRegistered()) {
        hideTab();
      } else {
        try { sessionStorage.setItem(CLOSED_KEY, '1'); } catch (e) {}
        revealTab();
      }
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus(); } catch (e) {}
      }
    }

    /* ---- Auto-open 7 seconds after the page opens ----
       Guarded by a session flag so once the popup is closed it won't keep
       popping up again during the same session. */
    var AUTO_KEY = 'early-access-autoopen-done';
    var AUTO_DELAY = 59000; // ms
    function autoOpenAllowed() {
      try { return sessionStorage.getItem(AUTO_KEY) !== '1'; } catch (e) { return true; }
    }
    function markAutoOpenDone() {
      try { sessionStorage.setItem(AUTO_KEY, '1'); } catch (e) {}
    }
    function setupAutoOpen() {
      if (!autoOpenAllowed()) return;
      if (window.Shopify && window.Shopify.designMode) return; // don't nag in the theme editor

      window.setTimeout(function () {
        if (autoOpenAllowed() && !modal.classList.contains('is-open')) {
          markAutoOpenDone();
          open();
        }
      }, AUTO_DELAY);
    }

    function onKeydown(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        close();
        return;
      }
      // simple focus trap
      if (e.key === 'Tab' || e.keyCode === 9) {
        var items = focusableIn(dialog);
        if (!items.length) return;
        var firstEl = items[0];
        var lastEl = items[items.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    openBtn.addEventListener('click', open);

    closeEls.forEach(function (el) {
      el.addEventListener('click', close);
    });

    // click on the backdrop (outside the dialog) closes
    modal.addEventListener('click', function (e) {
      if (e.target === modal) close();
    });

    setupAutoOpen();
    initSpots(root, modal);
  }

  /* =============================================================
     Spots-claimed progress bar (FOMO)
     --------------------------------------------------------------
     Shows "<claimed> / <total> מקומות נתפסו" with an animated bar.

     What the number is:
       claimed = base
               + per-day drip since the start date (identical for every
                 visitor on a given calendar day — not random)
               + 1 once THIS visitor completes the Flashy signup (persisted).

     NOTE — this is a display/social-proof counter, not Flashy's real
     global total. The theme cannot read Flashy's live subscriber count
     without the Flashy API (a server-side call, since the API key must
     stay secret). To show the TRUE live count, expose a Shopify App
     Proxy endpoint that queries Flashy list 38336 and returns the count,
     then fetch() it here and pass it into setClaimed(). Everything below
     already animates to whatever number you feed it.
     ============================================================= */
  function initSpots(root, modal) {
    var wrap = root.querySelector('[data-ea-progress]');
    if (!wrap) return;

    var mode = (wrap.getAttribute('data-mode') || 'auto').trim();
    var total = parseInt(wrap.getAttribute('data-total'), 10) || 200;
    var base = parseInt(wrap.getAttribute('data-base'), 10) || 0;
    var perDay = parseInt(wrap.getAttribute('data-per-day'), 10) || 0;
    var startStr = wrap.getAttribute('data-start') || '';
    var apiUrl = (wrap.getAttribute('data-api-url') || '').trim().replace(/\/+$/, '');
    var isCustom = mode === 'custom';

    // When the counter Worker URL is configured, this holds the REAL shared count.
    // Until then (or if the request fails) we fall back to the base + per-day display counter.
    var live = null;

    var claimedEl = wrap.querySelector('[data-ea-claimed]');
    var leftEl = wrap.querySelector('[data-ea-left]');
    var fillEl = wrap.querySelector('[data-ea-fill]');
    var barEl = wrap.querySelector('[data-ea-bar]');

    var CLAIM_KEY = 'early-access-claimed';
    function iJoined() {
      try { return localStorage.getItem(CLAIM_KEY) === '1'; } catch (e) { return false; }
    }
    function markJoined() {
      try { localStorage.setItem(CLAIM_KEY, '1'); } catch (e) {}
    }

    function dripCount() {
      if (perDay <= 0 || !startStr) return 0;
      var m = startStr.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (!m) return 0;
      var start = new Date(+m[1], (+m[2]) - 1, +m[3]).getTime();
      var days = Math.floor((Date.now() - start) / 86400000);
      if (days < 0) days = 0;
      return days * perDay;
    }

    function target() {
      // Custom mode: show exactly the number the merchant set in the customizer.
      // No drip, no live API, no ceiling games — the bar rests on that number.
      if (isCustom) {
        return Math.min(total, Math.max(0, base));
      }
      // Real shared count when available; base acts as a minimum floor.
      if (live !== null) {
        return Math.min(total, Math.max(live, base));
      }
      // Fallback display counter (no Worker configured / request failed).
      var n = base + dripCount() + (iJoined() ? 1 : 0);
      var ceiling = iJoined() ? total : Math.max(base, total - 8);
      if (n > ceiling) n = ceiling;
      if (n > total) n = total;
      return n;
    }

    // ---- shared counter API (GET current, POST +1) ----
    function apiGet() {
      if (!apiUrl) return Promise.resolve(null);
      return fetch(apiUrl, { method: 'GET', mode: 'cors', cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { if (j && typeof j.count === 'number') live = j.count; return live; })
        .catch(function () { return null; });
    }
    function apiHit() {
      if (!apiUrl) return Promise.resolve(null);
      return fetch(apiUrl, { method: 'POST', mode: 'cors', cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { if (j && typeof j.count === 'number') live = j.count; return live; })
        .catch(function () { return null; });
    }

    // ---- live polling: while the popup is open, refresh so the bar climbs as
    //      OTHER people sign up, with no page refresh ----
    var pollTimer = null;
    function startPolling() {
      if (!apiUrl || pollTimer) return;
      pollTimer = setInterval(function () {
        if (document.hidden) return;
        apiGet().then(function () { tweenTo(target(), false); });
      }, 4000);
    }
    function stopPolling() {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    }

    var shown = base;
    var raf = null;

    function paint(n) {
      var pct = total > 0 ? Math.max(0, Math.min(100, (n / total) * 100)) : 0;
      if (fillEl) fillEl.style.width = pct.toFixed(1) + '%';
      if (claimedEl) claimedEl.textContent = n;
      if (leftEl) {
        var left = Math.max(0, total - n);
        leftEl.textContent = left > 0 ? ('נותרו ' + left) : 'אזל!';
      }
      if (barEl) barEl.setAttribute('aria-valuenow', n);
      wrap.classList.toggle('is-hot', pct >= 82);
    }

    function tweenTo(to, bump) {
      if (raf) cancelAnimationFrame(raf);
      var from = shown;
      var startT = null;
      var dur = 950;
      function step(t) {
        if (startT === null) startT = t;
        var k = Math.min(1, (t - startT) / dur);
        var eased = 1 - Math.pow(1 - k, 3); // easeOutCubic
        var val = Math.round(from + (to - from) * eased);
        paint(val);
        if (k < 1) { raf = requestAnimationFrame(step); }
        else {
          shown = to;
          if (bump && claimedEl) {
            claimedEl.classList.remove('is-bump');
            void claimedEl.offsetWidth; // restart animation
            claimedEl.classList.add('is-bump');
          }
        }
      }
      raf = requestAnimationFrame(step);
    }

    // Animate from 0 the first time the modal opens (so it reads as "filling up"),
    // fetching the shared count first, then keep it live via polling.
    var animatedOnce = false;
    function animateIn() {
      if (animatedOnce) { startPolling(); return; }
      animatedOnce = true;
      shown = 0;
      apiGet().then(function () {
        tweenTo(target(), false);
        startPolling();
      });
    }
    if (modal.classList.contains('is-open')) animateIn();

    // Fire on every open; stop polling when the popup closes.
    var openObs = new MutationObserver(function () {
      if (modal.classList.contains('is-open')) animateIn();
      else stopPolling();
    });
    openObs.observe(modal, { attributes: true, attributeFilter: ['class'] });

    // Paint the resting state immediately (in case animations are reduced/skipped).
    paint(target());

    // Count the submit: increment the shared counter once per visitor.
    // The <form> "submit" event only fires after the browser's required-field
    // validation passes, so empty/invalid clicks don't count.
    var formEl = root.querySelector('.early-access-form');
    function countSubmit() {
      if (iJoined()) return;
      markJoined();
      if (apiUrl) {
        apiHit().then(function () { tweenTo(target(), true); });
      } else {
        tweenTo(target(), true); // display-only fallback: +1 locally
      }
    }
    if (formEl) formEl.addEventListener('submit', countSubmit);

    // Safety net: if Flashy swallows the submit event, still count when it
    // reveals its success block. countSubmit() is guarded, so no double count.
    var successEl = root.querySelector('.flashy-success');
    if (successEl) {
      var seen = !successEl.classList.contains('flashy-hidden');
      var succObs = new MutationObserver(function () {
        var visible = !successEl.classList.contains('flashy-hidden');
        if (visible && !seen) { seen = true; countSubmit(); }
      });
      succObs.observe(successEl, { attributes: true, attributeFilter: ['class'] });
    }

    // Manual override hook: window.__eaSetClaimed(n)
    window.__eaSetClaimed = function (n) {
      n = parseInt(n, 10);
      if (isNaN(n)) return;
      live = Math.min(n, total);
      tweenTo(target(), false);
    };
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
